import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';
import { validateEnv } from './utils/envValidation';
import { initGA, measureWebVitals } from './utils/analytics';
// Leaflet CSS loaded on-demand when HostelMap/LocationPicker mount to reduce initial payload
import * as Sentry from "@sentry/react";

// Validate environment at startup
validateEnv();
initGA();
measureWebVitals();

// Defer Sentry init to avoid blocking first paint
const deferInit = typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 0);
deferInit(() => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/hostelhunters\.com/, /^https:\/\/brkzsfbiuazrbmpnpzyl\.supabase\.co/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const message = error instanceof Error ? error.message : String(error ?? '');
      // Drop non-actionable browser View Transitions API errors — these are
      // triggered by the browser itself when layout shifts occur during a
      // navigation transition and cannot be fixed in application code.
      if (
        message.toLowerCase().includes('view transition') ||
        message.toLowerCase().includes('skiptransition')
      ) {
        return null;
      }
      return event;
    },
  });
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
